import * as THREE from 'three'

import Experience from '../Experience'

const SKY_VERTEX = /* glsl */ `
  varying vec3 vWorldPosition;

  void main() {
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

const SKY_FRAGMENT = /* glsl */ `
  varying vec3 vWorldPosition;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);

    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));

    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    mat2 r = mat2(1.62, 1.18, -1.18, 1.62);

    for (int i = 0; i < 5; i++) {
      v += noise(p) * a;
      p = r * p + 17.3;
      a *= 0.5;
    }

    return v;
  }

  void main() {
    vec3 dir = normalize(vWorldPosition - cameraPosition);
    float h = clamp(dir.y * 0.5 + 0.5, 0.0, 1.0);

    vec3 top = vec3(0.32, 0.56, 0.86);
    vec3 mid = vec3(0.68, 0.80, 0.92);
    vec3 horizon = vec3(0.86, 0.76, 0.58);
    vec3 sky = mix(horizon, mid, smoothstep(0.03, 0.46, h));
    sky = mix(sky, top, smoothstep(0.55, 1.0, h));

    vec3 sunDir = normalize(vec3(-0.56, 0.38, -0.74));
    float sun = max(dot(dir, sunDir), 0.0);
    sky += vec3(1.0, 0.64, 0.28) * pow(sun, 260.0) * 1.8;
    sky += vec3(1.0, 0.75, 0.42) * pow(sun, 18.0) * 0.18;

    float cloudDomain = fbm(dir.xz * 3.5 + vec2(dir.y * 2.0, 0.0));
    float cloudMask = smoothstep(0.58, 0.79, cloudDomain);
    cloudMask *= smoothstep(0.12, 0.46, dir.y) * (1.0 - smoothstep(0.82, 0.98, dir.y));
    vec3 cloud = mix(vec3(0.96, 0.88, 0.72), vec3(1.0), smoothstep(0.72, 0.92, cloudDomain));
    sky = mix(sky, cloud, cloudMask * 0.72);

    vec2 ring = normalize(dir.xz + vec2(0.0001));
    float ridge =
      0.015 +
      fbm(ring * 2.8 + vec2(2.0, 4.0)) * 0.16 +
      fbm(ring * 9.0 + vec2(9.0, 1.0)) * 0.035;
    float mountain = smoothstep(ridge + 0.025, ridge - 0.02, dir.y);
    mountain *= smoothstep(-0.24, -0.015, dir.y);
    vec3 farRock = vec3(0.33, 0.31, 0.27);
    vec3 litRock = vec3(0.60, 0.51, 0.37);
    float slope = fbm(ring * 12.0 + dir.y * 15.0);
    vec3 rock = mix(farRock, litRock, slope);
    sky = mix(sky, rock, mountain * 0.9);

    float desert = smoothstep(0.055, -0.16, dir.y);
    float duneNoise = fbm(dir.xz * 10.0 + vec2(4.0, 11.0));
    vec3 dune = mix(vec3(0.56, 0.48, 0.34), vec3(0.83, 0.73, 0.52), duneNoise);
    sky = mix(sky, dune, desert * (1.0 - mountain) * 0.95);

    float haze = smoothstep(-0.02, 0.2, abs(dir.y));
    sky = mix(vec3(0.82, 0.75, 0.62), sky, haze);

    gl_FragColor = vec4(sky, 1.0);
  }
`

/**
 * High-resolution procedural desert sky. It replaces the original low-res
 * GoldSrc TGA skybox while keeping the same infinite-box behaviour.
 */
export default class Sky {
  private readonly experience = Experience.instance
  private readonly mesh: THREE.Mesh

  constructor(name: string) {
    const material = new THREE.ShaderMaterial({
      vertexShader: SKY_VERTEX,
      fragmentShader: SKY_FRAGMENT,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      fog: false,
    })

    this.mesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), material)
    this.mesh.name = `procedural-sky-${name}`
    this.mesh.scale.setScalar(12000)
    this.mesh.renderOrder = -1000
    this.mesh.frustumCulled = false
    this.experience.scene.add(this.mesh)

    console.info(`[cs] procedural sky "${name}" mounted`)
  }

  /** Keep the sky centred on the camera so it reads as infinitely distant. */
  public update(): void {
    this.mesh.position.copy(this.experience.camera.instance.position)
  }
}
